import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Button, message } from "src/assets/imports/antdComponents";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/store";
import { setIsProfileContextModalVisible } from "./states/profileContextSlice";
import { getProfileContext, updateProfileContext } from "./serviceHelper";
import "./style.css";
import { CHAR_LENGTH, PATTERN } from "src/config/dataConfig";
import { validateLanguageInput } from "../Studies/transformer";
import ProfileContextSkeleton from "./components/ProfileContextSkeleton";

const ProfileContext = () => {
	const dispatch = useDispatch();
	const [form] = Form.useForm();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [initialContext, setInitialContext] = useState<string>("");
	const { isProfileContextModalVisible } = useSelector((state: RootState) => state.profileContextSlice);
	const { supportedLanguages } = useSelector((state: RootState) => state.studiesSlice);
	const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

	// Fetch profile context when modal opens
	useEffect(() => {
		if (isProfileContextModalVisible) {
			fetchProfileContext();
		}
	}, [isProfileContextModalVisible]);

	const getUserFriendlyErrorMessage = (error: any) => {
		if (!error?.response) {
			return "Network error. Please check your connection and try again.";
		}

		const status = error.response.status;

		switch (status) {
			case 401:
			case 403:
				return "You do not have permission to access profile context.";
			case 404:
				return "Profile context configuration not found.";
			case 500:
				return "Something went wrong on the server. Please try again later.";
			default:
				return "Unable to load profile context. Please try again.";
		}
	};

	const fetchProfileContext = async () => {
		try {
			setIsLoading(true);

			const response = await getProfileContext();

			const context = response?.data?.profile_context ?? "";

			setInitialContext(context);
			form.setFieldsValue({ profileContext: context });
		} catch (error: any) {
			message.error(getUserFriendlyErrorMessage(error));
		} finally {
			setIsLoading(false);
		}
	};

	const checkFormValidity = () => {
		const profileContext = form.getFieldValue("profileContext");

		const hasChanged = profileContext?.trim() !== initialContext;

		const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);

		const isValid = profileContext && profileContext.trim().length > 0 && !hasErrors;

		setIsSubmitEnabled(hasChanged && isValid);
	};

	const handleClose = () => {
		form.resetFields();
		setInitialContext("");
		dispatch(setIsProfileContextModalVisible(false));
	};

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields();
			setIsSubmitting(true);

			const payload = {
				profile_context: values.profileContext.trim(),
			};

			await updateProfileContext(payload);

			message.success("Profile context saved successfully");
			setInitialContext(values.profileContext.trim());
			form.resetFields();
		} catch (error: any) {
			console.error("Validation or submission failed:", error);
			if (error?.response) {
				message.error(error?.response?.data?.detail || "Failed to save profile context");
			} else if (error?.errorFields) {
				// Form validation error - don't show message
				return;
			} else {
				message.error("An unexpected error occurred");
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const validateProfileContext = async (_: any, value: string) => {
		// Trim whitespace for validation
		const trimmedValue = value.trim();
		if (!trimmedValue) {
			return Promise.reject("Profile context cannot be empty");
		}

		// Max length validation (5000 characters)
		if (value.length > CHAR_LENGTH.XXXL) {
			return Promise.reject(`Profile context cannot exceed ${CHAR_LENGTH.XXXL} characters`);
		}

		// Use existing language validation with FLEXIBLE pattern
		const allowedSpecialChars = PATTERN.FLEXIBLE;
		const errorMessage = validateLanguageInput(supportedLanguages, value, allowedSpecialChars);
		if (errorMessage) {
			return Promise.reject(new Error(errorMessage + PATTERN.FLEXIBLE));
		}

		return Promise.resolve();
	};

	const isFormValid = () => {
		const profileContext = form.getFieldValue("profileContext");
		const hasChanged = profileContext?.trim() !== initialContext;
		const isValid = profileContext && profileContext.trim().length > 0 && !form.getFieldsError().some(({ errors }) => errors.length);
		return hasChanged && isValid;
	};

	return (
		<Modal
			destroyOnClose
			open={isProfileContextModalVisible}
			onCancel={handleClose}
			footer={null}
			width="100vw"
			className="profile-context-modal"
			style={{
				top: "5.5rem",
				left: 0,
				margin: 0,
				padding: 0,
				maxWidth: "100vw",
				height: "calc(100vh - 5.5rem)",
				overflow: "hidden",
				borderRadius: 0,
			}}
			maskStyle={{ backgroundColor: "rgba(0,0,0,0)" }}
			styles={{
				body: {
					width: "100vw",
					padding: "1.5rem 2rem",
					height: "calc(100vh - 5.5rem)",
				},
				content: {
					borderRadius: 0,
					padding: 0,
				},
			}}>
			<div className="flex flex-col h-full">
				{/* Header */}
				<div className="text-xl font-semibold primary-color mb-8">Set Profile Context</div>

				{/* Form Container - Centered with max-width */}
				<div className="flex-1 flex justify-center overflow-hidden">
					<div
						style={{
							width: "100%",
							maxWidth: "75rem",
							paddingTop: "2rem",
							display: "flex",
							flexDirection: "column",
							height: "100%",
						}}>
						{isLoading ? (
							<ProfileContextSkeleton />
						) : (
							<>
								<Form form={form} layout="vertical" onValuesChange={checkFormValidity}>
									<Form.Item
										style={{ flex: 1, display: "flex", flexDirection: "column" }}
										label="Profile Context"
										name="profileContext"
										className="text-base-14"
										rules={[{ validator: validateProfileContext }]}>
										<Input.TextArea
											autoComplete="off"
											allowClear
											showCount
											maxLength={CHAR_LENGTH.XXXL}
											placeholder="Describe what this profile represents, and the type of data that will be uploaded. Include any relevant scope, focus areas, preferences, or constraints to help Kural understand and use this profile effectively."
											className="profile-context-textarea text-base-14"
											disabled={isSubmitting}
										/>
									</Form.Item>
								</Form>

								{/* Footer Buttons */}
								<div className="flex justify-end space-x-4 mt-8">
									<Button onClick={handleClose} disabled={isSubmitting}>
										Cancel
									</Button>
									<Button type="primary" onClick={handleSubmit} loading={isSubmitting} disabled={isSubmitting || !isSubmitEnabled}>
										Submit
									</Button>
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		</Modal>
	);
};

export default React.memo(ProfileContext);
